
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flight } from "@/types/flight";


interface AirportSelectorProps {
  flights: Flight[];
}

// PNG airport codes - major airports in Papua New Guinea
const PNG_AIRPORT_CODES = [
  'POM', // Port Moresby - Jacksons International Airport
  'LAE', // Lae - Lae Airport
  'MAG', // Madang - Madang Airport
  'HGU', // Mount Hagen - Mount Hagen Airport
  'WWK', // Wewak - Wewak Airport
  'DAU', // Daru - Daru Airport
  'KVG', // Kavieng - Kavieng Airport
  'RAB', // Rabaul - Tokua Airport
  'GUR', // Gurney - Gurney Airport
  'KER', // Kerema - Kerema Airport
  'MIS', // Misima - Misima Airport
  'UNG', // Kiunga - Kiunga Airport
  'CMU', // Chimbu - Kundiawa Airport
  'TFI', // Tufi - Tufi Airport
  'KMA', // Kerema - Kerema Airport
  'SIL', // Sila - Sila Airport
  'TAB', // Tabubil - Tabubil Airport
  'VAN', // Vanimo - Vanimo Airport
  'PNP', // Popondetta - Girua Airport
  'LSA', // Losuia - Losuia Airport
];

export function AirportSelector({ flights }: AirportSelectorProps) {
  // Get unique airports from flights (both departure and arrival cities)
  const airportMap = new Map<string, { name: string; code: string; departureCount: number; arrivalCount: number }>();

  flights.forEach(flight => {
    // Add departure city if it's a PNG airport
    if (flight.departure_city && PNG_AIRPORT_CODES.includes(flight.departure_city.code)) {
      const existing = airportMap.get(flight.departure_city.code) || {
        name: flight.departure_city.name,
        code: flight.departure_city.code,
        departureCount: 0,
        arrivalCount: 0
      };
      existing.departureCount++;
      airportMap.set(flight.departure_city.code, existing);
    }

    // Add arrival city if it's a PNG airport
    if (flight.arrival_city && PNG_AIRPORT_CODES.includes(flight.arrival_city.code)) {
      const existing = airportMap.get(flight.arrival_city.code) || {
        name: flight.arrival_city.name,
        code: flight.arrival_city.code,
        departureCount: 0,
        arrivalCount: 0
      };
      existing.arrivalCount++;
      airportMap.set(flight.arrival_city.code, existing);
    }
  });

  const airports = Array.from(airportMap.values()).sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  if (airports.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-foreground">No PNG airports found in current flights</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
          Select PNG Airport
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {airports.map((airport) => (
            <Link key={airport.code} to={`/airport/${airport.code}`}>
              <Button 
                variant="outline" 
                className="w-full h-auto p-4 flex flex-col items-start space-y-1 bg-status-ontime hover:bg-muted"
              >
                <div className="font-semibold text-left">
                  {airport.name}
                </div>
                <div className="text-sm text-foreground">
                  {airport.code}
                </div>
                <div className="text-xs text-foreground mt-1">
                  {airport.departureCount} departures • {airport.arrivalCount} arrivals
                </div>
              </Button>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
